import React, {
  KeyboardEventHandler,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { Textarea } from "@mui/joy";
import { isMobile } from "react-device-detect";
import { theme } from "styles/theme";

interface TextareaOnboardingProps {
  placeholder?: string;
  initialValue?: string;
  handleChange: (value: string) => void;
  isFocused: boolean;
  setIsFocused: React.Dispatch<React.SetStateAction<boolean>>;
  emojiDisabled?: boolean;
  readOnly?: boolean;
  inputRef?: RefObject<HTMLTextAreaElement>;
  textAlign: "start" | "center";
  numbersOnly?: boolean;
  maxLength?: number;
}

export default function TextareaOnboarding({
  placeholder,
  initialValue,
  handleChange,
  isFocused,
  setIsFocused,
  emojiDisabled,
  readOnly,
  inputRef,
  textAlign,
  numbersOnly,
  maxLength,
}: TextareaOnboardingProps) {
  const [value, setValue] = useState(initialValue);
  // IME 조합(한글 자모 결합) 진행 중 여부
  const isComposingRef = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      setValue(initialValue);
    }
  }, [initialValue, isFocused]);

  const handleKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (
      !isMobile &&
      (e.code === "Enter" ||
        e.code === "NumpadEnter" ||
        e.key === "Enter" ||
        e.key === "NumpadEnter")
    ) {
      // 필요 시 엔터 입력 로직
    }
  };

  // 허용 범위 ㄱ-힣(U+3131~U+D7A3) 안에 ㆍ(아래아, U+318D)가 포함되므로 별도로 제거
  const disabled_regex = /[^a-zA-Z0-9ㄱ-힣]|ㆍ/g;

  const sanitize = (text: string) => {
    // ➌ numbersOnly가 true이면 숫자 이외의 문자를 제거
    if (numbersOnly) {
      return text.replace(/[^0-9]/g, "");
    }
    // 그렇지 않고 emojiDisabled가 true이면 기존 로직 수행
    if (emojiDisabled) {
      return text.replace(disabled_regex, "");
    }
    return text;
  };

  const applyValue = (text: string) => {
    setValue(text);
    handleChange(text);
  };

  return (
    <Textarea
      placeholder={placeholder}
      value={value || ""}
      maxRows={1}
      onChange={(e) => {
        const raw = e.target.value;
        // 조합 중에는 값을 가공하지 않는다. 가공한 값으로 DOM 을 덮어쓰면 IME 조합이
        // 끊겨 자모가 분리된다 (예: 천지인 ㆍ 입력 중 "ㅎㆍ" → "ㅎ" 로 덮어써서 "호" 가
        // "ㅎ호" 가 됨). 가공은 조합이 끝나는 onCompositionEnd 에서 한다.
        applyValue(isComposingRef.current ? raw : sanitize(raw));
      }}
      readOnly={readOnly || false}
      minRows={1}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      onKeyDown={handleKeyDown}
      slotProps={{
        textarea: {
          ref: inputRef,
          onCompositionStart: () => {
            isComposingRef.current = true;
          },
          onCompositionEnd: (e) => {
            isComposingRef.current = false;
            applyValue(sanitize(e.currentTarget.value));
          },
          // ➍ numbersOnly일 때 모바일에서 숫자 키패드를 띄우도록 설정
          inputMode: numbersOnly ? "numeric" : "text",
          maxLength: maxLength,
        },
      }}
      sx={{
        width: "100%",
        boxShadow: "none",
        backgroundColor: "transparent",
        padding: "0 20px",
        border: "none",
        minWidth: 0,
        minHeight: "24px",
        "--joy-focus-thickness": "0px",
        "--variant-borderWidth": "0px",
        "--Textarea-gap": "0px",
        "& textarea": {
          textAlign: `${textAlign}`,
          padding: 0,
          margin: 0,
          fontWeight: 500,
          color: theme.vars.palette.Content.Strong,
          whiteSpace: "nowrap",
          overflowX: "auto",
          resize: "none",
        },
        "& textarea::placeholder": {
          textAlign: `${textAlign}`,
        },
      }}
    />
  );
}
